import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  BatteryFull as BatteryIcon,
  SignalCellularAlt as SignalIcon,
  Build as BuildIcon,
  Sensors as SensorsIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/PageHeader';
import StatusChip from '../../components/StatusChip';
import LoadingSkeleton from '../../components/LoadingSkeleton';
import { useConfirm } from '../../hooks/useConfirm';
import { useSensorStore } from '../../stores/sensor.store';
import { useOrganizationStore } from '../../stores/organization.store';
import { SensorType, SensorStatus, UpdateSensorRequest, SensorRequest } from '../../types/sensor.type';

const IoTensors: React.FC = () => {
  const { t } = useTranslation();
  const { confirm, confirmDialog } = useConfirm();
  const { sensors, isLoading, getSensors, createSensor, updateSensor, deleteSensor, calibrateSensor } = useSensorStore();
  const { selectedOrganization } = useOrganizationStore();
  const [page] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState('');
  const [type, setType] = useState<SensorType | ''>('');
  const [status, setStatus] = useState<SensorStatus | ''>('');
  const [showModal, setShowModal] = useState(false);
  const [selectedSensor, setSelectedSensor] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    type: SensorType.FILL_LEVEL,
    serial_number: '',
    model: '',
    manufacturer: '',
    installation_date: '',
    last_calibration_date: '',
    next_calibration_date: '',
    battery_level: 100,
    signal_strength: 100,
    container_id: '',
  });

  useEffect(() => {
    if (selectedOrganization) {
      loadSensors();
    }
  }, [selectedOrganization, page, limit, search, type, status]);

  const loadSensors = async () => {
    if (selectedOrganization) {
      await getSensors(selectedOrganization.id, {
        page,
        limit,
        search: search || undefined,
        type: type || undefined,
        status: status || undefined,
      });
    }
  };

  const handleCreateSensor = async () => {
    if (selectedOrganization) {
      const sensorData: SensorRequest = {
        name: formData.name,
        type: formData.type,
        serial_number: formData.serial_number,
        model: formData.model,
        manufacturer: formData.manufacturer,
        installation_date: formData.installation_date,
        container_id: formData.container_id || undefined,
        organization_id: selectedOrganization.id,
      };
      await createSensor(sensorData);
      setShowModal(false);
      resetForm();
    }
  };

  const handleUpdateSensor = async () => {
    if (selectedSensor) {
      const updateData: UpdateSensorRequest = {
        name: formData.name || undefined,
        type: formData.type || undefined,
        status: selectedSensor.status,
        last_calibration_date: formData.last_calibration_date || undefined,
        next_calibration_date: formData.next_calibration_date || undefined,
        container_id: formData.container_id || undefined,
      };
      await updateSensor(selectedSensor.id, updateData);
      setShowModal(false);
      setSelectedSensor(null);
    }
  };

  const handleDeleteSensor = async (sensorId: string) => {
    if (await confirm({ title: t('common.delete'), description: t('iotSensors.confirmDelete'), tone: 'danger' })) {
      await deleteSensor(sensorId);
    }
  };

  const handleCalibrateSensor = async (sensorId: string) => {
    if (await confirm({ title: t('common.calibrate'), description: t('iotSensors.confirmCalibrate') })) {
      await calibrateSensor(sensorId);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: SensorType.FILL_LEVEL,
      serial_number: '',
      model: '',
      manufacturer: '',
      installation_date: '',
      last_calibration_date: '',
      next_calibration_date: '',
      battery_level: 100,
      signal_strength: 100,
      container_id: '',
    });
  };

  const getBatteryColor = (level: number) => {
    if (level > 75) return 'success';
    if (level > 25) return 'warning';
    return 'error';
  };

  const getSignalColor = (strength: number) => {
    if (strength > 75) return 'success';
    if (strength > 25) return 'warning';
    return 'error';
  };

  if (isLoading) {
    return <LoadingSkeleton variant="cards" filters={3} cardCount={8} />;
  }

  return (
    <Box>
      <PageHeader
        icon={<SensorsIcon />}
        title={t('iotSensors.title')}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedSensor(null);
              setShowModal(true);
            }}
          >
            {t('iotSensors.addSensor')}
          </Button>
        }
      />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={t('common.search')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('common.type')}</InputLabel>
                <Select
                  value={type}
                  label={t('common.type')}
                  onChange={(e) => setType(e.target.value as SensorType)}
                >
                  <MenuItem value="">{t('common.all')}</MenuItem>
                  {Object.values(SensorType).map((sensorType) => (
                    <MenuItem key={sensorType} value={sensorType}>
                      {sensorType}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('common.status')}</InputLabel>
                <Select
                  value={status}
                  label={t('common.status')}
                  onChange={(e) => setStatus(e.target.value as SensorStatus)}
                >
                  <MenuItem value="">{t('common.all')}</MenuItem>
                  {Object.values(SensorStatus).map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {sensors.map((sensor) => (
          <Grid item xs={12} sm={6} md={4} key={sensor.id}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {sensor.name}
                    </Typography>
                    <Chip
                      label={sensor.type}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    <StatusChip label={sensor.status} value={sensor.status} chipSize="sm" />
                  </Box>
                  <Box>
                    <Tooltip title={t('common.edit')}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedSensor(sensor);
                          setFormData({
                            name: sensor.name,
                            type: sensor.type,
                            serial_number: sensor.serial_number,
                            model: sensor.model,
                            manufacturer: sensor.manufacturer,
                            installation_date: sensor.installation_date,
                            last_calibration_date: sensor.last_calibration_date || '',
                            next_calibration_date: sensor.next_calibration_date || '',
                            battery_level: sensor.battery_level || 0,
                            signal_strength: sensor.signal_strength || 0,
                            container_id: sensor.container_id || '',
                          });
                          setShowModal(true);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('iotSensors.calibrate')}>
                      <IconButton
                        size="small"
                        onClick={() => handleCalibrateSensor(sensor.id)}
                      >
                        <BuildIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('common.delete')}>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteSensor(sensor.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Box mt={2}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <BatteryIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                    {t('common.batteryLevel')}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={sensor.battery_level ?? 0}
                    color={getBatteryColor(sensor.battery_level ?? 0)}
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <SignalIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                    {t('common.signalStrength')}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={sensor.signal_strength ?? 0}
                    color={getSignalColor(sensor.signal_strength ?? 0)}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" mt={2}>
                  {t('iotSensors.serialLabel', { value: sensor.serial_number })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('iotSensors.modelLabel', { value: sensor.model })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('iotSensors.lastCalibrationLabel', { value: sensor.last_calibration_date ? new Date(sensor.last_calibration_date).toLocaleDateString() : t('common.notAvailable') })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('iotSensors.nextCalibrationLabel', { value: sensor.next_calibration_date ? new Date(sensor.next_calibration_date).toLocaleDateString() : t('common.notAvailable') })}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedSensor ? t('iotSensors.editDialogTitle') : t('iotSensors.addDialogTitle')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('common.name')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('common.type')}</InputLabel>
                  <Select
                    value={formData.type}
                    label={t('common.type')}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as SensorType })}
                  >
                    {Object.values(SensorType).map((sensorType) => (
                      <MenuItem key={sensorType} value={sensorType}>
                        {sensorType}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('common.serialNumber')}
                  value={formData.serial_number}
                  onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('common.model')}
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('common.manufacturer')}
                  value={formData.manufacturer}
                  onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t('common.installationDate')}
                  type="date"
                  value={formData.installation_date}
                  onChange={(e) => setFormData({ ...formData, installation_date: e.target.value })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t('common.lastCalibrationDate')}
                  type="date"
                  value={formData.last_calibration_date}
                  onChange={(e) => setFormData({ ...formData, last_calibration_date: e.target.value })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t('common.nextCalibrationDate')}
                  type="date"
                  value={formData.next_calibration_date}
                  onChange={(e) => setFormData({ ...formData, next_calibration_date: e.target.value })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('common.batteryLevel')}
                  type="number"
                  value={formData.battery_level}
                  onChange={(e) => setFormData({ ...formData, battery_level: Number(e.target.value) })}
                  InputProps={{
                    inputProps: { min: 0, max: 100 }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('common.signalStrength')}
                  type="number"
                  value={formData.signal_strength}
                  onChange={(e) => setFormData({ ...formData, signal_strength: Number(e.target.value) })}
                  InputProps={{
                    inputProps: { min: 0, max: 100 }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('common.containerId')}
                  value={formData.container_id}
                  onChange={(e) => setFormData({ ...formData, container_id: e.target.value })}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowModal(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={selectedSensor ? handleUpdateSensor : handleCreateSensor}
          >
            {selectedSensor ? t('common.update') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>
      {confirmDialog}
    </Box>
  );
};

export default IoTensors; 